// AdminReportsDialog.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Button,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";
import safeToast from "utils/safeToast";
import axiosInstance from "API/axiosInstance";

// Get staffId and token from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null,
          token: parsed.Token || parsed.token || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null, token: null };
};

const { staffId, token } = getAdminSession();

const AdminReportsDialog = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states - using arrays for multi-select
  const [registrationType, setRegistrationType] = useState(["0"]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  // Filter options
  const registrationOptions = [
    { value: "0", label: "All Alumni" },
    { value: "1", label: "Registered Users" },
    { value: "2", label: "Unregistered Alumni" }
  ];

  // Note: All dropdown options are now loaded from database via API

  // Dropdown options loaded from database
  const [dropdownOptions, setDropdownOptions] = useState({
    batches: [],
    genders: [],
    colleges: [],
    courses: [],
    branches: [],
    statuses: [],
    organizations: [],
    designations: [],
    locations: [],
    availabilities: [],
    domains: [],
    categories: []
  });

  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Fetch dropdown options from database
  const fetchDropdownOptions = useCallback(async () => {
    try {
      setLoadingDropdowns(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_FILTER_DROPDOWN_OPTIONS);
      const data = response.data || {};
      
      setDropdownOptions({
        batches: (data.batches || []).map(item => ({ value: item.value, label: item.label })),
        genders: (data.genders || []).map(item => ({ value: item.value, label: item.label })),
        colleges: (data.colleges || []).map(item => ({ value: item.value, label: item.label })),
        courses: (data.courses || []).map(item => ({ value: item.value, label: item.label })),
        branches: (data.branches || []).map(item => ({ value: item.value, label: item.label })),
        statuses: (data.statuses || []).map(item => ({ value: item.value, label: item.label })),
        organizations: (data.organizations || []).map(item => ({ value: item.value, label: item.label })),
        designations: (data.designations || []).map(item => ({ value: item.value, label: item.label })),
        locations: (data.locations || []).map(item => ({ value: item.value, label: item.label })),
        availabilities: (data.availabilities || []).map(item => ({ value: item.value, label: item.label })),
        domains: (data.domains || []).map(item => ({ value: item.value, label: item.label })),
        categories: (data.categories || []).map(item => ({ value: item.value, label: item.label }))
      });
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
      safeToast.error("Failed to load filter options. Please try again.");
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Column definitions for AG Grid
  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      minWidth: 50,
      maxWidth: 70,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { textAlign: "center", fontSize: '0.7rem' },
    },
    { field: "fullName", headerName: "Full Name", sortable: true, filter: true, flex: 2, minWidth: 150 },
    { field: "rollnum", headerName: "Roll Number", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "customGender", headerName: "Gender", sortable: true, filter: true, flex: 1, minWidth: 100 },
    { 
      field: "customDob", 
      headerName: "Date of Birth", 
      sortable: true, 
      filter: true, 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => params.data?.customDob || params.data?.CustomDob || ""
    },
    { field: "customBatch", headerName: "Batch", sortable: true, filter: true, flex: 1, minWidth: 80 },
    { field: "customCollegeStudied", headerName: "College", sortable: true, filter: true, flex: 2, minWidth: 150 },
    { field: "customCourseStudied", headerName: "Course", sortable: true, filter: true, flex: 1.5, minWidth: 120 },
    { field: "customBranch", headerName: "Branch", sortable: true, filter: true, flex: 1, minWidth: 100 },
    { field: "customWhatsappNumber", headerName: "WhatsApp", sortable: true, filter: true, flex: 1.5, minWidth: 120 },
    { field: "customPresentStatus", headerName: "Status", sortable: true, filter: true, flex: 1.5, minWidth: 150 },
    { field: "customOrganization", headerName: "Organization", sortable: true, filter: true, flex: 2, minWidth: 180 },
    { field: "customDesignation", headerName: "Designation", sortable: true, filter: true, flex: 1.5, minWidth: 140 },
    { field: "customLocation", headerName: "Location", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "customAvailability", headerName: "Availability", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "domain", headerName: "Domain", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "customCategory", headerName: "Category", sortable: true, filter: true, flex: 1, minWidth: 120 },
  ], []);

  // Grid defaults
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
    cellStyle: { fontSize: '0.7rem' },
  }), []);

  // Fetch reports data with filters - convert arrays to comma-separated strings for API
  const fetchReportsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.GET_ALUMNI_REPORTS_WITH_FILTERS, {
        registedType: Array.isArray(registrationType) ? registrationType.join(',') : registrationType,
        batch: Array.isArray(selectedBatch) ? selectedBatch.join(',') : selectedBatch,
        gender: Array.isArray(selectedGender) ? selectedGender.join(',') : selectedGender,
        college: Array.isArray(selectedCollege) ? selectedCollege.join(',') : selectedCollege,
        course: Array.isArray(selectedCourse) ? selectedCourse.join(',') : selectedCourse,
        branch: Array.isArray(selectedBranch) ? selectedBranch.join(',') : selectedBranch,
        status: Array.isArray(selectedStatus) ? selectedStatus.join(',') : selectedStatus,
        organization: Array.isArray(selectedOrganization) ? selectedOrganization.join(',') : selectedOrganization,
        designation: Array.isArray(selectedDesignation) ? selectedDesignation.join(',') : selectedDesignation,
        location: Array.isArray(selectedLocation) ? selectedLocation.join(',') : selectedLocation,
        availability: Array.isArray(selectedAvailability) ? selectedAvailability.join(',') : selectedAvailability,
        domain: Array.isArray(selectedDomain) ? selectedDomain.join(',') : selectedDomain,
        category: Array.isArray(selectedCategory) ? selectedCategory.join(',') : selectedCategory
      });
      setRowData(response.data || []);
    } catch (error) {
      console.error("Error fetching reports data:", error);
      safeToast.error("Failed to fetch reports data. Please try again.");
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [
    registrationType,
    selectedBatch,
    selectedGender,
    selectedCollege,
    selectedCourse,
    selectedBranch,
    selectedStatus,
    selectedOrganization,
    selectedDesignation,
    selectedLocation,
    selectedAvailability,
    selectedDomain,
    selectedCategory
  ]);

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit({ defaultMinWidth: 100 });
  }, []);

  // Export CSV function
  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      safeToast.warn('No data to export');
      return;
    }

    try {
      const headers = ['S.No', 'Full Name', 'Roll Number', 'Gender', 'Date of Birth', 'Batch', 'College', 'Course', 'Branch', 'WhatsApp', 'Status', 'Organization', 'Designation', 'Location', 'Availability', 'Domain', 'Category'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.rollnum || '').replace(/"/g, '""')}"`,
          `"${(row.customGender || '').replace(/"/g, '""')}"`,
          `"${((row.customDob || row.CustomDob) || '').replace(/"/g, '""')}"`,
          `"${(row.customBatch || '').replace(/"/g, '""')}"`,
          `"${(row.customCollegeStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customCourseStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customBranch || '').replace(/"/g, '""')}"`,
          `"${(row.customWhatsappNumber || '').replace(/"/g, '""')}"`,
          `"${(row.customPresentStatus || '').replace(/"/g, '""')}"`,
          `"${(row.customOrganization || '').replace(/"/g, '""')}"`,
          `"${(row.customDesignation || '').replace(/"/g, '""')}"`,
          `"${(row.customLocation || '').replace(/"/g, '""')}"`,
          `"${(row.customAvailability || '').replace(/"/g, '""')}"`,
          `"${(row.domain || '').replace(/"/g, '""')}"`,
          `"${(row.customCategory || '').replace(/"/g, '""')}"`,
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `alumni-reports-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      safeToast.error('Failed to export CSV. Please try again.');
    }
  }, [rowData]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setRegistrationType(["0"]);
    setSelectedBatch([]);
    setSelectedGender([]);
    setSelectedCollege([]);
    setSelectedCourse([]);
    setSelectedBranch([]);
    setSelectedStatus([]);
    setSelectedOrganization([]);
    setSelectedDesignation([]);
    setSelectedLocation([]);
    setSelectedAvailability([]);
    setSelectedDomain([]);
    setSelectedCategory([]);
  }, []);

  // Count active filters - count non-empty arrays
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    const regType = Array.isArray(registrationType) ? registrationType : [registrationType];
    if (regType.length > 0 && !regType.includes("0")) count++;
    if (Array.isArray(selectedBatch) && selectedBatch.length > 0) count++;
    if (Array.isArray(selectedGender) && selectedGender.length > 0) count++;
    if (Array.isArray(selectedCollege) && selectedCollege.length > 0) count++;
    if (Array.isArray(selectedCourse) && selectedCourse.length > 0) count++;
    if (Array.isArray(selectedBranch) && selectedBranch.length > 0) count++;
    if (Array.isArray(selectedStatus) && selectedStatus.length > 0) count++;
    if (Array.isArray(selectedOrganization) && selectedOrganization.length > 0) count++;
    if (Array.isArray(selectedDesignation) && selectedDesignation.length > 0) count++;
    if (Array.isArray(selectedLocation) && selectedLocation.length > 0) count++;
    if (Array.isArray(selectedAvailability) && selectedAvailability.length > 0) count++;
    if (Array.isArray(selectedDomain) && selectedDomain.length > 0) count++;
    if (Array.isArray(selectedCategory) && selectedCategory.length > 0) count++;
    return count;
  }, [
    registrationType,
    selectedBatch,
    selectedGender,
    selectedCollege,
    selectedCourse,
    selectedBranch,
    selectedStatus,
    selectedOrganization,
    selectedDesignation,
    selectedLocation,
    selectedAvailability,
    selectedDomain,
    selectedCategory
  ]);

  // Load dropdown options and data when dialog opens
  useEffect(() => {
    if (open) {
      fetchDropdownOptions();
      fetchReportsData();
    }
  }, [open, fetchDropdownOptions]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Title + Close Button */}
      <DialogTitle sx={{ pr: 8, fontWeight: "bold", color: "primary.main" }}>
        Alumni Reports - Advanced Search
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content - Split Layout */}
      <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100vh - 64px)' }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: '100%', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Left Side - Filter Panel */}
            <Paper 
              elevation={2}
              sx={{ 
                width: 380,
                minWidth: 350,
                maxWidth: '30%',
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Filter Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'success.main', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon />
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Filter Options
                    </Typography>
                  </Box>
                  {activeFiltersCount > 0 && (
                    <Chip 
                      label={activeFiltersCount} 
                      size="small" 
                      sx={{ 
                        bgcolor: 'white',
                        color: 'success.main',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Active Filters Summary */}
              {activeFiltersCount > 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'primary.lighter', borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark', fontSize: '0.75rem' }}>
                      {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', py: 0.5 }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Filter Content - Scrollable */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem' }}>
                  Select filters to refine your search
                </Typography>
                <Grid container spacing={2}>
                {/* Registration Type */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={registrationOptions}
                    value={registrationOptions.filter(opt => 
                      Array.isArray(registrationType) 
                        ? registrationType.includes(opt.value)
                        : registrationType === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setRegistrationType(values.length > 0 ? values : ["0"]);
                    }}
                    getOptionLabel={(option) => option.label || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Registration Type"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Batch */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.batches}
                    value={dropdownOptions.batches.filter(opt => 
                      Array.isArray(selectedBatch) 
                        ? selectedBatch.includes(opt.value)
                        : selectedBatch === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedBatch(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Batch"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.genders}
                    value={dropdownOptions.genders.filter(opt => 
                      Array.isArray(selectedGender) 
                        ? selectedGender.includes(opt.value)
                        : selectedGender === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedGender(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Gender"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* College */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    freeSolo
                    loading={loadingDropdowns}
                    options={dropdownOptions.colleges}
                    value={Array.isArray(selectedCollege) 
                      ? selectedCollege.map(col => typeof col === 'string' ? { value: col, label: col } : col)
                      : selectedCollege ? [{ value: selectedCollege, label: selectedCollege }] : []
                    }
                    onChange={(event, newValue) => {
                      const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                      setSelectedCollege(values);
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="College"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Course */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.courses}
                    value={dropdownOptions.courses.filter(opt => 
                      Array.isArray(selectedCourse) 
                        ? selectedCourse.includes(opt.value)
                        : selectedCourse === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedCourse(values);
                    }}
                    getOptionLabel={(option) => option.label || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Course"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Branch */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.branches}
                    value={dropdownOptions.branches.filter(opt => 
                      Array.isArray(selectedBranch) 
                        ? selectedBranch.includes(opt.value)
                        : selectedBranch === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedBranch(values);
                    }}
                    getOptionLabel={(option) => option.label || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Branch"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.statuses}
                    value={dropdownOptions.statuses.filter(opt => 
                      Array.isArray(selectedStatus) 
                        ? selectedStatus.includes(opt.value)
                        : selectedStatus === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedStatus(values);
                    }}
                    getOptionLabel={(option) => option.label || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Organization */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    freeSolo
                    loading={loadingDropdowns}
                    options={dropdownOptions.organizations}
                    value={Array.isArray(selectedOrganization) 
                      ? selectedOrganization.map(org => typeof org === 'string' ? { value: org, label: org } : org)
                      : selectedOrganization ? [{ value: selectedOrganization, label: selectedOrganization }] : []
                    }
                    onChange={(event, newValue) => {
                      const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                      setSelectedOrganization(values);
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Organization"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Designation */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    freeSolo
                    loading={loadingDropdowns}
                    options={dropdownOptions.designations}
                    value={Array.isArray(selectedDesignation) 
                      ? selectedDesignation.map(des => typeof des === 'string' ? { value: des, label: des } : des)
                      : selectedDesignation ? [{ value: selectedDesignation, label: selectedDesignation }] : []
                    }
                    onChange={(event, newValue) => {
                      const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                      setSelectedDesignation(values);
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Location */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    freeSolo
                    loading={loadingDropdowns}
                    options={dropdownOptions.locations}
                    value={Array.isArray(selectedLocation) 
                      ? selectedLocation.map(loc => typeof loc === 'string' ? { value: loc, label: loc } : loc)
                      : selectedLocation ? [{ value: selectedLocation, label: selectedLocation }] : []
                    }
                    onChange={(event, newValue) => {
                      const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                      setSelectedLocation(values);
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Location"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Availability */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.availabilities}
                    value={dropdownOptions.availabilities.filter(opt => 
                      Array.isArray(selectedAvailability) 
                        ? selectedAvailability.includes(opt.value)
                        : selectedAvailability === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedAvailability(values);
                    }}
                    getOptionLabel={(option) => option.label || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Availability"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Domain */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.domains}
                    value={dropdownOptions.domains.filter(opt => 
                      Array.isArray(selectedDomain) 
                        ? selectedDomain.includes(opt.value)
                        : selectedDomain === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedDomain(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Domain"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Category */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.categories}
                    value={dropdownOptions.categories.filter(opt => 
                      Array.isArray(selectedCategory) 
                        ? selectedCategory.includes(opt.value)
                        : selectedCategory === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedCategory(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              </Box>

              {/* Filter Footer with Action Buttons */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button                  
                    size="medium"
                    fullWidth
                    startIcon={<RefreshIcon />}
                    onClick={fetchReportsData}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    Apply Filters
                  </Button>
                  <Button                 
                    size="medium"
                    fullWidth
                    onClick={clearAllFilters}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Right Side - Data Grid */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Top Action Bar */}
              <Paper sx={{ m: 2, mb: 1, p: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {rowData.length} record{rowData.length !== 1 ? 's' : ''} found
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button                  
                      size="small"          
                      startIcon={<RefreshIcon />}
                      onClick={fetchReportsData}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Refresh
                    </Button>
                    <Button                 
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportCsv}
                      disabled={rowData.length === 0}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Export CSV
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* AG Grid */}
              <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2 }}>
                <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    animateRows={true}
                    suppressRowClickSelection={true}
                    rowHeight={28}
                    headerHeight={35}
                    pagination={true}
                    paginationPageSize={15}
                    suppressMenuHide={true}
                  />
                </div>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

AdminReportsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminReportsDialog;

